import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock as FaStatusClock,
  FaArrowRight,
  FaThumbsUp,
  FaThumbsDown,
  FaSearch,
} from "react-icons/fa";
import { fetchPotholes } from "../../store/potholeSlice";
import { STATUSES } from "../../globals/misc/statuses";

const AllPotholes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { potholes, status } = useSelector((state) => state.pothole);
  const [search, setSearch] = useState("");
  const [severity, setSeverity] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    dispatch(fetchPotholes());
  }, [dispatch]);

  const filtered = useMemo(() => {
    return potholes.filter((p) => {
      const matchesSearch =
        !search ||
        p.address?.toLowerCase().includes(search.toLowerCase()) ||
        p.description?.toLowerCase().includes(search.toLowerCase());
      const matchesSeverity = severity === "all" || p.severity === severity;
      const matchesStatus = statusFilter === "all" || p.status === statusFilter;
      return matchesSearch && matchesSeverity && matchesStatus;
    });
  }, [potholes, search, severity, statusFilter]);

  const stats = useMemo(
    () => ({
      total: potholes.length,
      inProgress: potholes.filter((p) => p.status === "in-progress").length,
      fixed: potholes.filter((p) => p.status === "fixed").length,
      high: potholes.filter((p) => p.severity === "high").length,
    }),
    [potholes]
  );

  const getSeverityStyles = (value) => {
    switch (value) {
      case "high":
        return "bg-red-500 text-white";
      case "medium":
        return "bg-amber-500 text-white";
      case "low":
        return "bg-emerald-500 text-white";
      default:
        return "bg-slate-500 text-white";
    }
  };

  const getStatusIcon = (value) => {
    switch (value) {
      case "in-progress":
        return <FaExclamationTriangle className="text-amber-500" />;
      case "verified":
      case "fixed":
        return <FaCheckCircle className="text-emerald-500" />;
      default:
        return <FaStatusClock className="text-navy-700" />;
    }
  };

  if (status === STATUSES.LOADING && potholes.length === 0) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-14 w-14 animate-spin rounded-full border-b-2 border-navy-800" />
      </div>
    );
  }

  if (status === STATUSES.ERROR && potholes.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <div className="rounded-2xl border border-red-200 bg-red-50 p-10">
          <FaExclamationTriangle className="mx-auto mb-4 text-4xl text-red-500" />
          <p className="text-lg font-medium text-red-700">
            Failed to load potholes
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      <section className="relative overflow-hidden bg-gradient-to-br from-navy-950 via-navy-900 to-navy-700 text-white">
        <div className="pointer-events-none absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_20%_20%,rgba(251,191,36,0.35),transparent_40%),radial-gradient(circle_at_80%_0%,rgba(255,255,255,0.12),transparent_35%)]" />
        <div className="relative mx-auto max-w-6xl px-4 py-14 md:py-16">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-amber-400">
            Community map
          </p>
          <h1 className="font-display text-4xl font-bold md:text-5xl">
            All reported potholes
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-white/75">
            Browse hazards reported by neighbors, filter by severity, and open a
            report to vote or comment.
          </p>
        </div>
      </section>

      <div className="relative z-10 mx-auto max-w-6xl px-4 pb-4 pt-6 sm:pt-8">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: "Total reports", value: stats.total, tone: "text-navy-800" },
            {
              label: "In progress",
              value: stats.inProgress,
              tone: "text-amber-600",
            },
            { label: "Fixed", value: stats.fixed, tone: "text-emerald-600" },
            { label: "High severity", value: stats.high, tone: "text-red-600" },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-navy-900/10 bg-white/95 p-5 shadow-[0_12px_40px_rgba(11,31,68,0.08)] backdrop-blur"
            >
              <div className={`font-display text-3xl font-bold ${item.tone}`}>
                {item.value}
              </div>
              <div className="mt-1 text-sm text-navy-800/65">{item.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-navy-900/10 bg-white p-4 shadow-sm md:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-navy-800/40" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by address or description..."
                className="w-full rounded-xl border border-navy-900/10 bg-surface py-2.5 pl-10 pr-4 outline-none transition focus:border-navy-700 focus:ring-2 focus:ring-navy-700/15"
              />
            </div>
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value)}
              className="rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700"
            >
              <option value="all">All severity</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-xl border border-navy-900/10 bg-surface px-4 py-2.5 outline-none focus:border-navy-700"
            >
              <option value="all">All status</option>
              <option value="reported">Reported</option>
              <option value="verified">Verified</option>
              <option value="in-progress">In progress</option>
              <option value="fixed">Fixed</option>
            </select>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="mt-10 rounded-2xl border border-navy-900/10 bg-white px-6 py-16 text-center">
            <FaMapMarkerAlt className="mx-auto mb-4 text-4xl text-navy-700/35" />
            <h3 className="font-display text-2xl font-semibold text-navy-900">
              No matching reports
            </h3>
            <p className="mt-2 text-navy-800/65">
              Try clearing filters or be the first to report nearby.
            </p>
            <button
              type="button"
              onClick={() => navigate("/report")}
              className="mt-6 rounded-xl bg-amber-500 px-6 py-2.5 font-semibold text-navy-950 hover:bg-amber-400"
            >
              Report a pothole
            </button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((pothole) => (
              <article
                key={pothole._id}
                onClick={() => navigate(`/pothole/${pothole._id}`)}
                className="group cursor-pointer overflow-hidden rounded-2xl border border-navy-900/10 bg-white shadow-[0_10px_30px_rgba(11,31,68,0.06)] transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_22px_50px_rgba(11,31,68,0.14)]"
              >
                <div className="relative h-44 overflow-hidden bg-surface sm:h-52">
                  {pothole.images?.url ? (
                    <img
                      src={pothole.images.url}
                      alt={`Pothole at ${pothole.address}`}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-navy-700/30">
                      <FaMapMarkerAlt className="text-4xl" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-navy-950/10 to-transparent" />
                  <span
                    className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${getSeverityStyles(
                      pothole.severity
                    )}`}
                  >
                    {pothole.severity || "unknown"}
                  </span>
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-2">
                    <h3 className="font-display text-lg font-semibold leading-snug text-white line-clamp-2">
                      {pothole.address || "Unknown location"}
                    </h3>
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-xs text-white backdrop-blur">
                      {getStatusIcon(pothole.status)}
                      <span className="capitalize">
                        {pothole.status?.replace("-", " ") || "unknown"}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  <p className="min-h-[2.75rem] text-sm leading-relaxed text-navy-800/70 line-clamp-2">
                    {pothole.description || "No description provided."}
                  </p>

                  <div className="mt-4 flex items-center justify-between border-t border-navy-900/8 pt-4 text-sm">
                    <div className="flex items-center gap-3 text-navy-800/70">
                      <span className="inline-flex items-center gap-1 text-emerald-600">
                        <FaThumbsUp /> {pothole.votes?.upvotes || 0}
                      </span>
                      <span className="inline-flex items-center gap-1 text-red-500">
                        <FaThumbsDown /> {pothole.votes?.downvotes || 0}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-1 text-navy-800/55">
                      <FaClock className="text-xs" />
                      {new Date(pothole.createdAt).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-navy-800 transition group-hover:text-amber-600">
                    View details
                    <FaArrowRight className="text-xs transition group-hover:translate-x-1" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AllPotholes;
